import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { useAuth } from "./AuthContext";

const RealtimeContext = createContext(null);

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.trim();

export function RealtimeProvider({ children }) {
  const { user, loading } = useAuth();

  const socketRef = useRef(null);

  const [socket, setSocket] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [conversationTyping, setConversationTyping] = useState({});

  const fetchNotificationCount = useCallback(async () => {
    if (!user?._id) {
      setUnreadNotifications(0);
      return 0;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/notifications/me`, {
        withCredentials: true,
      });

      const unread =
        res?.data?.data?.unreadCount ??
        res?.data?.unreadCount ??
        res?.data?.count ??
        0;

      const next = Number(unread || 0);
      setUnreadNotifications(next);
      return next;
    } catch (err) {
      console.error("fetchNotificationCount error:", err);
      setUnreadNotifications(0);
      return 0;
    }
  }, [user?._id]);

  const fetchMessageCount = useCallback(async () => {
    if (!user?._id) {
      setUnreadMessages(0);
      return 0;
    }

    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/chat/unread-count`, {
        withCredentials: true,
      });

      const totalUnread =
        res?.data?.data?.unreadCount ??
        res?.data?.unreadCount ??
        0;

      const next = Number(totalUnread || 0);
      setUnreadMessages(next);
      return next;
    } catch (err) {
      console.error("fetchMessageCount error:", err);
      setUnreadMessages(0);
      return 0;
    }
  }, [user?._id]);

  const refreshUnreadCounts = useCallback(async () => {
    await Promise.all([fetchNotificationCount(), fetchMessageCount()]);
  }, [fetchNotificationCount, fetchMessageCount]);

  useEffect(() => {
    if (loading) return;

    if (!user?._id) {
      setUnreadNotifications(0);
      setUnreadMessages(0);
      setConversationTyping({});
      setIsSocketConnected(false);

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      setSocket(null);
      return;
    }

    refreshUnreadCounts();

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    const s = io(API_BASE_URL, {
      withCredentials: true,
      transports: ["websocket", "polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
    });

    socketRef.current = s;
    setSocket(s);

    const handleReconnectRefresh = async () => {
      await refreshUnreadCounts();
    };

    const handleTyping = (payload) => {
      const fromUserId = payload?.fromUserId;
      const isTyping = Boolean(payload?.isTyping);

      if (!fromUserId) return;

      setConversationTyping((prev) => ({
        ...prev,
        [String(fromUserId)]: isTyping,
      }));
    };

    const handleUnreadCountUpdated = (payload) => {
      const unreadCount = Number(payload?.unreadCount || 0);

      if (payload?.conversationId) {
        fetchMessageCount();
        return;
      }

      setUnreadMessages(unreadCount);
    };

    s.on("connect", () => {
      setIsSocketConnected(true);
      handleReconnectRefresh();
    });

    s.on("disconnect", () => {
      setIsSocketConnected(false);
    });

    s.on("reconnect", handleReconnectRefresh);

    s.on("notification:new", fetchNotificationCount);
    s.on("notification:read", fetchNotificationCount);
    s.on("notification:all-read", fetchNotificationCount);

    s.on("chat:new_message", fetchMessageCount);
    s.on("chat:message_sent", fetchMessageCount);
    s.on("chat:message_delivered", fetchMessageCount);
    s.on("chat:messages_delivered", fetchMessageCount);
    s.on("chat:messages_read", fetchMessageCount);
    s.on("chat:message_edited", fetchMessageCount);
    s.on("chat:message_deleted", fetchMessageCount);
    s.on("chat:message_reaction_updated", fetchMessageCount);
    s.on("chat:unread_count_updated", handleUnreadCountUpdated);
    s.on("chat:typing", handleTyping);

    return () => {
      s.off("connect");
      s.off("disconnect");
      s.off("reconnect", handleReconnectRefresh);

      s.off("notification:new", fetchNotificationCount);
      s.off("notification:read", fetchNotificationCount);
      s.off("notification:all-read", fetchNotificationCount);

      s.off("chat:new_message", fetchMessageCount);
      s.off("chat:message_sent", fetchMessageCount);
      s.off("chat:message_delivered", fetchMessageCount);
      s.off("chat:messages_delivered", fetchMessageCount);
      s.off("chat:messages_read", fetchMessageCount);
      s.off("chat:message_edited", fetchMessageCount);
      s.off("chat:message_deleted", fetchMessageCount);
      s.off("chat:message_reaction_updated", fetchMessageCount);
      s.off("chat:unread_count_updated", handleUnreadCountUpdated);
      s.off("chat:typing", handleTyping);

      s.disconnect();

      if (socketRef.current === s) {
        socketRef.current = null;
      }

      setSocket(null);
      setIsSocketConnected(false);
      setConversationTyping({});
    };
  }, [
    user?._id,
    loading,
    refreshUnreadCounts,
    fetchNotificationCount,
    fetchMessageCount,
  ]);

  const value = useMemo(
    () => ({
      socket,
      isSocketConnected,
      unreadNotifications,
      unreadMessages,
      conversationTyping,
      refreshUnreadCounts,
      refreshMessageCount: fetchMessageCount,
      refreshNotificationCount: fetchNotificationCount,
      setUnreadMessages,
      setUnreadNotifications,
    }),
    [
      socket,
      isSocketConnected,
      unreadNotifications,
      unreadMessages,
      conversationTyping,
      refreshUnreadCounts,
      fetchMessageCount,
      fetchNotificationCount,
    ]
  );

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error("useRealtime must be used inside RealtimeProvider");
  }
  return ctx;
}









// import React, {
//   createContext,
//   useCallback,
//   useContext,
//   useEffect,
//   useMemo,
//   useRef,
//   useState,
// } from "react";
// import { io } from "socket.io-client";
// import axios from "axios";
// import { useAuth } from "./AuthContext";

// const RealtimeContext = createContext(null);

// const API_BASE_URL =
//   process.env.REACT_APP_API_BASE_URL;

// export function RealtimeProvider({ children }) {
//   const { user, loading } = useAuth();

//   const socketRef = useRef(null);

//   const [socket, setSocket] = useState(null);
//   const [isSocketConnected, setIsSocketConnected] = useState(false);
//   const [unreadNotifications, setUnreadNotifications] = useState(0);
//   const [unreadMessages, setUnreadMessages] = useState(0);

//   const fetchNotificationCount = useCallback(async () => {
//     if (!user?._id) {
//       setUnreadNotifications(0);
//       return 0;
//     }

//     try {
//       const res = await axios.get(`${API_BASE_URL}/api/v1/notifications/me`, {
//         withCredentials: true,
//       });

//       const unread =
//         res?.data?.data?.unreadCount ??
//         res?.data?.unreadCount ??
//         res?.data?.count ??
//         0;

//       const next = Number(unread || 0);
//       setUnreadNotifications(next);
//       return next;
//     } catch (err) {
//       console.error("fetchNotificationCount error:", err);
//       setUnreadNotifications(0);
//       return 0;
//     }
//   }, [user?._id]);

//   const fetchMessageCount = useCallback(async () => {
//     if (!user?._id) {
//       setUnreadMessages(0);
//       return 0;
//     }

//     try {
//       const res = await axios.get(`${API_BASE_URL}/api/v1/chat/list`, {
//         withCredentials: true,
//       });

//       const list = Array.isArray(res?.data?.data) ? res.data.data : [];

//       const totalUnread = list.reduce(
//         (sum, item) => sum + Number(item?.unreadCount || 0),
//         0
//       );

//       setUnreadMessages(totalUnread);
//       return totalUnread;
//     } catch (err) {
//       console.error("fetchMessageCount error:", err);
//       setUnreadMessages(0);
//       return 0;
//     }
//   }, [user?._id]);

//   const refreshUnreadCounts = useCallback(async () => {
//     await Promise.all([fetchNotificationCount(), fetchMessageCount()]);
//   }, [fetchNotificationCount, fetchMessageCount]);

//   const setTypingInConversation = useCallback(
//     ({ toUserId, isTyping }) => {
//       if (!socketRef.current || !user?._id || !toUserId) return;

//       socketRef.current.emit("chat:typing", {
//         fromUserId: user._id,
//         toUserId,
//         isTyping: Boolean(isTyping),
//       });
//     },
//     [user?._id]
//   );

//   useEffect(() => {
//     if (loading) return;

//     if (!user?._id) {
//       setUnreadNotifications(0);
//       setUnreadMessages(0);
//       setIsSocketConnected(false);

//       if (socketRef.current) {
//         socketRef.current.removeAllListeners();
//         socketRef.current.disconnect();
//         socketRef.current = null;
//       }

//       setSocket(null);
//       return;
//     }

//     refreshUnreadCounts();

//     if (socketRef.current) {
//       socketRef.current.removeAllListeners();
//       socketRef.current.disconnect();
//       socketRef.current = null;
//     }

//     const s = io(API_BASE_URL, {
//       withCredentials: true,
//       transports: ["websocket", "polling"],
//       autoConnect: true,
//       reconnection: true,
//       reconnectionAttempts: Infinity,
//       reconnectionDelay: 1000,
//     });

//     socketRef.current = s;
//     setSocket(s);

//     const joinUserRoom = () => {
//       s.emit("chat:join", { userId: user._id });
//     };

//     const handleReconnectRefresh = async () => {
//       joinUserRoom();
//       await refreshUnreadCounts();
//     };

//     s.on("connect", () => {
//       setIsSocketConnected(true);
//       handleReconnectRefresh();
//     });

//     s.on("disconnect", () => {
//       setIsSocketConnected(false);
//     });

//     s.on("reconnect", handleReconnectRefresh);

//     s.on("notification:new", fetchNotificationCount);
//     s.on("notification:read", fetchNotificationCount);
//     s.on("notification:all-read", fetchNotificationCount);

//     s.on("chat:new-message", fetchMessageCount);
//     s.on("chat:messages-read", fetchMessageCount);
//     s.on("chat:message-updated", fetchMessageCount);
//     s.on("chat:message-deleted", fetchMessageCount);
//     s.on("chat:message-reaction", fetchMessageCount);

//     return () => {
//       s.off("connect");
//       s.off("disconnect");
//       s.off("reconnect", handleReconnectRefresh);

//       s.off("notification:new", fetchNotificationCount);
//       s.off("notification:read", fetchNotificationCount);
//       s.off("notification:all-read", fetchNotificationCount);

//       s.off("chat:new-message", fetchMessageCount);
//       s.off("chat:messages-read", fetchMessageCount);
//       s.off("chat:message-updated", fetchMessageCount);
//       s.off("chat:message-deleted", fetchMessageCount);
//       s.off("chat:message-reaction", fetchMessageCount);

//       s.disconnect();

//       if (socketRef.current === s) {
//         socketRef.current = null;
//       }

//       setSocket(null);
//       setIsSocketConnected(false);
//     };
//   }, [
//     user?._id,
//     loading,
//     refreshUnreadCounts,
//     fetchNotificationCount,
//     fetchMessageCount,
//   ]);

//   const value = useMemo(
//     () => ({
//       socket,
//       isSocketConnected,
//       unreadNotifications,
//       unreadMessages,
//       refreshUnreadCounts,
//       refreshMessageCount: fetchMessageCount,
//       refreshNotificationCount: fetchNotificationCount,
//       setUnreadMessages,
//       setUnreadNotifications,
//       setTypingInConversation,
//     }),
//     [
//       socket,
//       isSocketConnected,
//       unreadNotifications,
//       unreadMessages,
//       refreshUnreadCounts,
//       fetchMessageCount,
//       fetchNotificationCount,
//       setTypingInConversation,
//     ]
//   );

//   return (
//     <RealtimeContext.Provider value={value}>
//       {children}
//     </RealtimeContext.Provider>
//   );
// }

// export function useRealtime() {
//   const ctx = useContext(RealtimeContext);
//   if (!ctx) {
//     throw new Error("useRealtime must be used inside RealtimeProvider");
//   }
//   return ctx;
// }